import { supabase, supabaseAdmin } from '../lib/supabaseClient';

interface BannerRow {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  design_emoji: string | null;
  design_background_color: string | null;
  design_text_color: string | null;
  target_url: string | null;
  order_priority: number;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface BannerRecord {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string;
  designEmoji: string | null;
  designBackgroundColor: string | null;
  designTextColor: string | null;
  targetUrl: string | null;
  orderPriority: number;
  isActive: boolean;
  validFrom: string | null;
  validUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

const mapBanner = (row: BannerRow): BannerRecord => ({
  id: row.id,
  title: row.title,
  description: row.description,
  imageUrl: row.image_url,
  designEmoji: row.design_emoji,
  designBackgroundColor: row.design_background_color,
  designTextColor: row.design_text_color,
  targetUrl: row.target_url,
  orderPriority: row.order_priority,
  isActive: Boolean(row.is_active),
  validFrom: row.valid_from,
  validUntil: row.valid_until,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export async function listBanners(): Promise<BannerRecord[]> {
  const client = supabaseAdmin ?? supabase;

  const { data, error } = await client
    .from('banners')
    .select('*')
    .order('order_priority', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).map(mapBanner);
}

export async function listActiveBanners(): Promise<BannerRecord[]> {
  const client = supabaseAdmin ?? supabase;
  const now = new Date().toISOString();

  const { data, error } = await client
    .from('banners')
    .select('*')
    .eq('is_active', true)
    .or(`valid_from.is.null,valid_from.lte.${now}`)
    .or(`valid_until.is.null,valid_until.gte.${now}`)
    .order('order_priority', { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).map(mapBanner);
}

export interface UpsertBannerInput {
  id?: string;
  title: string;
  description?: string | null;
  imageUrl: string;
  designEmoji?: string | null;
  designBackgroundColor?: string | null;
  designTextColor?: string | null;
  targetUrl?: string | null;
  orderPriority?: number;
  isActive?: boolean;
  validFrom?: string | null;
  validUntil?: string | null;
}

export async function upsertBanner(input: UpsertBannerInput): Promise<BannerRecord> {
  const client = supabaseAdmin ?? supabase;
  const payload = {
    title: input.title,
    description: input.description ?? null,
    image_url: input.imageUrl,
    design_emoji: input.designEmoji ?? null,
    design_background_color: input.designBackgroundColor ?? null,
    design_text_color: input.designTextColor ?? null,
    target_url: input.targetUrl ?? null,
    order_priority: input.orderPriority ?? 0,
    is_active: input.isActive ?? true,
    valid_from: input.validFrom ?? null,
    valid_until: input.validUntil ?? null,
  };

  const query = input.id
    ? client
        .from('banners')
        .update(payload)
        .eq('id', input.id)
        .select()
        .single()
    : client
        .from('banners')
        .insert([payload])
        .select()
        .single();

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return mapBanner(data as BannerRow);
}

export async function deleteBanner(bannerId: string): Promise<void> {
  const client = supabaseAdmin ?? supabase;
  const { error } = await client.from('banners').delete().eq('id', bannerId);
  if (error) {
    throw error;
  }
}

export async function setBannerActive(bannerId: string, isActive: boolean): Promise<void> {
  const client = supabaseAdmin ?? supabase;
  const { error } = await client
    .from('banners')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', bannerId);
  if (error) {
    throw error;
  }
}

export async function updateBannerOrder(bannerId: string, orderPriority: number): Promise<void> {
  const client = supabaseAdmin ?? supabase;
  const { error } = await client
    .from('banners')
    .update({ order_priority: orderPriority, updated_at: new Date().toISOString() })
    .eq('id', bannerId);
  if (error) {
    throw error;
  }
}
